/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';
import { Company, CompanyContact, CompanyGroup } from 'private/app/models/account-admin-search';
import { CompanyRelationshipsResult, PlaceHolderText } from 'private/app/models/account-admin-company.model';
import { BehaviorSubject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Option } from '../../../../common/components/select/select.component';
import { ApiOptionsService } from 'common/services/api-options/api-options.service';
import { ApiOptions } from '../../../../common/services/api-options/api-options.service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from 'common/environments/environment';
import { CountryForMasking } from 'private/app/services/account-admin/account-admin-company-create.service';
import { UserDetailsEntity } from 'private/app/models/user.model';
import { AppConstants } from 'common/app-constants';

export const RESIDENTIAL = 'RESR';

@Injectable({ providedIn: 'root' })
export class AccountAdminCompanyUtilsService {
    public userListCurrentPage: number = 1;
    public isuserListPageLoading$ = new BehaviorSubject<boolean>(true);
    public isCreateGroupPopUpVisible: boolean = false;
    public userPageSize: number = 10;
    public totalUsers: number;
    public radix: number = 10;
    public totalPages: number;
    public menuActive: boolean = false;
    public userUnderSelectedCompanyList$ = new BehaviorSubject<UserDetailsEntity[]>([]);
    public filterListUserUnderSelectedCompany$ = new BehaviorSubject<UserDetailsEntity[]>([]);
    public placeHolderData$: BehaviorSubject<PlaceHolderText> = new BehaviorSubject<PlaceHolderText>({
        placeHolderPhone: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.PHONE_NUMBER'),
        placeHolderOffice: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.OFFICE_NUMBER'),
        placeHolderMobile: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.MOBILE_NUMBER'),
        placeHolderFax: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.FAX_NUMBER'),
        placeHolderTollFree: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.TOLL_FREE_NUMBER'),
        placeHolderZipcode: { label: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.ZIPCODE') }
    });

    constructor(
        private translatePipe: TranslateService,
        private readonly apiOptions: ApiOptionsService,
        private readonly httpClient: HttpClient
    ) {
    }

    missingCoordinate(form: UntypedFormGroup): string {
        const { latitude, longitude } = form.value;

        return !latitude === !longitude ? '' : (latitude ? 'LONGITUDE' : 'LATITUDE');
    }

    updateComapnyCreateByForm(form: UntypedFormGroup, company: Company) {
        company.name = form.controls.companyName.value;
        company.url = form.controls.companyUrl.value;
        company.soldToCustomerId = form.controls.soldTo.value;
        company.shipToCustomerId = form.controls.shipTo.value;
        company.payerCustomerId = form.controls.payer.value;
        company.nationalAccountNo = form.controls.nationalAccountNumber.value;
        company.priceListType = form.controls.priceList.value;
        company.priceGroup = form.controls.priceGroup.value;
        company.contractorLicense = form.controls.contLic.value;

        company.addressLine1 = form.controls.address1.value;
        company.addressLine2 = form.controls.address2.value;
        company.city = form.controls.city.value;
        company.postalCode = form.controls.postalCode.value;
        company.phoneNumber = form.controls.phone.value;
        company.mobileNum = form.controls.mobile.value;
        company.tollFreeNumber = form.controls.tollFree.value;
        company.faxNumber = form.controls.fax.value;
        company.emailAddress = form.controls.email.value;

        company.c10Parent = form.controls.c10Parent.value;
        company.c10Child = form.controls.c10Child.value;
        company.storeNumber = form.controls.storeNumber.value;
        company.searchRadius = form.controls.searchRadius.value;
        company.divRegNum = form.controls.divRegNumber.value;
        company.ownershipType = form.controls.ownership.value;
        company.latitude = form.controls.latitude.value;
        company.longitude = form.controls.longitude.value;
        company.locatorName = form.controls.locatorName.value;
        company.salesOfficeNumber = form.controls.salesOfficeNumber.value;
        company.notes = form.controls.notes.value;

        // IT Super Admin
        company.inventoryAPI = form.controls.inventoryAPI.value;
        company.regionCode = form.controls.regionCode.value;
        company.customerUserName = form.controls.customerUserName.value;
        company.customerGUID = form.controls.customerGUID.value;
        company.group = this.mapSelectedGroup(form.controls.groupName.value);
    }

    updateRelationshipToCompany(relationshipForm: CompanyRelationshipsResult, company: Company) {
        company.brandedRelationships = relationshipForm.brandedRelationships;
        company.pricingWarrantyRelationships = relationshipForm.pricingWarrantyRelationships;
    }

    idmUserRole(idmUserRoles: string[]): IdmUserRoles {
        if (idmUserRoles.includes(IdmUserRoles.INTERNALCBP)) {
            return IdmUserRoles.INTERNALCBP;
        }
        if (idmUserRoles.includes(IdmUserRoles.INTERNALICP)) {
            return IdmUserRoles.INTERNALICP;
        }

        return IdmUserRoles.ACCOUNTADMIN;
    }

    formInputPlaceHolder(countryCode: string) {
        if (countryCode === 'US' || countryCode === 'CA') {
            const phoneKey = countryCode === 'US' ? 'PHONE_US' : 'PHONE_CA';
            const zipcodeKey = countryCode === 'US' ? 'ZIPCODE_US' : 'ZIPCODE_CA';
            this.placeHolderData$.next({
                placeHolderPhone: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${phoneKey}`),
                placeHolderOffice: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${phoneKey}`),
                placeHolderMobile: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${phoneKey}`),
                placeHolderFax: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${phoneKey}`),
                placeHolderTollFree: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${phoneKey}`),
                placeHolderZipcode: {
                    label: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${zipcodeKey}.LABEL`),
                    info: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${zipcodeKey}.INFO`)
                }
            });
        }
        else {
            const zipcodeKey = countryCode === 'MX' ? 'ZIPCODE_MX' : 'ZIPCODE';
            const zipcodeConfig = countryCode === 'MX' ?
                {
                    label: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${zipcodeKey}.LABEL`),
                    info: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${zipcodeKey}.INFO`)
                } :
                { label: this.translatePipe.instant(`ACCOUNT_ADMIN.PLACEHOLDER_TEXT.${zipcodeKey}`) };
            this.placeHolderData$.next({
                placeHolderPhone: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.PHONE_NUMBER'),
                placeHolderOffice: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.OFFICE_NUMBER'),
                placeHolderMobile: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.MOBILE_NUMBER'),
                placeHolderFax: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.FAX_NUMBER'),
                placeHolderTollFree: this.translatePipe.instant('ACCOUNT_ADMIN.PLACEHOLDER_TEXT.TOLL_FREE_NUMBER'),
                placeHolderZipcode: zipcodeConfig
            });
        }
    }

    public resetForm(form: UntypedFormGroup) {
        form.reset({
            services: '',
            brands: '',
            rsm: '',
            partRSM: '',
            accountManagers: '',
            partAM: '',
            tsm: '',
            pricingAnalyst: '',
            country: '',
            state: '',
            language: '',
            groupName: ''
        });
    }

    public updateCompanyContact(option: Option[], companyType: string, companyCreateCopy: Company) {
        if (!companyCreateCopy) {
            return;
        }

        if (!option?.length || !option[0]?.value || option[0].value === '' || option[0].value === 'select') {
            companyCreateCopy.companyContacts = companyCreateCopy.companyContacts.filter(
                (contact) => contact.contactType !== companyType
            );

            return;
        }

        const selectedOption = option[0];

        const existingContactIndex = companyCreateCopy.companyContacts
            .findIndex((contact) => contact.contactType === companyType);

        const newContact: CompanyContact = {
            contactType: companyType,
            userName: selectedOption.name,
            userId: selectedOption.value
        };

        if (existingContactIndex === -1) {
            companyCreateCopy.companyContacts.push(newContact);
        }
        else {
            companyCreateCopy.companyContacts[existingContactIndex] = newContact;
        }
    }


    public getRegionCodeByState(region: string, stateOptionData: Option[]): string {
        const stateSelection = stateOptionData.find((state) => state.name === region);
        if (stateSelection) {
            return stateSelection.value;
        }

        return '';
    }

    public getCountryCodeByCountry(countryName: string, countryOptionData: Option[]): string {
        const countrySelection = countryOptionData.find((country) => country.name === countryName);
        if (countrySelection) {
            return countrySelection.value;
        }

        return '';
    }

    public filterCountryArraywithMostUsed(countries: Option[] | undefined): Option[] {
        const mostUsedCountry = countries?.filter((country) => country.value === 'US' || country.value === 'CA' || country.value === 'MX') || [];
        mostUsedCountry.sort((optionA: Option, _optionB: Option) => {
            if (optionA.value === 'US') {
                return -1;
            }
            else if (optionA.value === 'CA') {
                return 1;
            }

            return 0;
        });
        const popDefaultItem = countries?.find((country) => country.value === '');
        if (popDefaultItem) {
            mostUsedCountry.unshift(popDefaultItem);
        }

        return [...mostUsedCountry, ...countries || []].filter((value, idx, array) => array.findIndex((value2: Option) => (value2.value === value.value)) === idx);
    }

    public isNumber(value: string | number): boolean {
        return ((value !== null) &&
               (value !== '') &&
               !isNaN(Number(value.toString())));
    }

    public getAccountAdminUsersByCompanyId(id: string) {
        this.isuserListPageLoading$.next(true);
        const params = new HttpParams({ fromString: `hvacCompanyId=${id}` });

        this.apiOptions.getAuthedHttpOptions().pipe(
            switchMap((options: ApiOptions) => this.httpClient.get<UserDetailsEntity[]>(`${environment.api.accountAdminUsersByCompany}`, {
                params,
                ...options
            })),
            catchError((err: HttpErrorResponse) => {
                this.resetUserData();
                if (err.status !== 404) {
                    throw err;
                }

                return [];
            })
        ).subscribe((userList: UserDetailsEntity[]) => {
            this.totalUsers = userList.length;
            this.userUnderSelectedCompanyList$.next(userList);
            this.totalPages = Math.ceil(this.totalUsers / this.userPageSize);
            this.handleCreateUserListPageChange(this.userListCurrentPage);
            this.isuserListPageLoading$.next(false);
        });
    }

    public resetUserData() {
        this.isuserListPageLoading$.next(false);
        this.userUnderSelectedCompanyList$.next([]);
        this.filterListUserUnderSelectedCompany$.next([]);
        this.totalPages = 0;
        this.totalUsers = 0;
    }

    public toggleMenu() {
        this.menuActive = !this.menuActive;
    }

    public handleCreateUserListPageChange(page:number) {
        this.userListCurrentPage = parseInt(page.toString(), this.radix);
        this.filterListUserUnderSelectedCompany$.next(
            this.userUnderSelectedCompanyList$.value.slice((this.userListCurrentPage - 1) * this.userPageSize, this.userListCurrentPage * this.userPageSize)
        );
    }

    public updatePostalCodeValidator(form: UntypedFormGroup, countryCode: string) {
        if (countryCode === CountryForMasking.US || countryCode === CountryForMasking.USA) {
            form.controls.postalCode.setValidators([
                Validators.required,
                Validators.pattern(AppConstants.US_ZIP_REGEX)
            ]);
        }
        else if (countryCode === CountryForMasking.CA || countryCode === CountryForMasking.CANADA) {
            form.controls.postalCode.setValidators([
                Validators.required,
                Validators.pattern(AppConstants.CANADA_ZIP_REGEX)
            ]);
        }
        else {
            form.controls.postalCode.setValidators([
                Validators.required
            ]);
        }
        form.controls.postalCode.updateValueAndValidity();
    }

    public createGroupPopUp() {
        this.isCreateGroupPopUpVisible = true;
    }

    public modalVisibilityChanged(event: boolean) {
        this.isCreateGroupPopUpVisible = event;
    }

    public mapSelectedGroup(selected: Option | Option[] | string | null | undefined): CompanyGroup | null {
        const option = Array.isArray(selected) ? selected[0] : selected;
        if (!option || typeof option !== 'object' || !option.value) {
            return null;
        }

        return {
            name: option.name,
            id: option.value
        };
    }

    public isGroupMappingCleared(previousGroup?: CompanyGroup | null, newGroup?: CompanyGroup | null): boolean {
        return Boolean(previousGroup?.id) && !newGroup?.id;
    }

    public normalizeCompanyGroup(group?: { id?: string | number; name?: string; groupName?: string } | null): CompanyGroup | null {
        if (!group) {
            return null;
        }

        const groupId = group.id;
        if (typeof groupId === 'undefined' || groupId === null || groupId === '') {
            return null;
        }

        const name = group.name || group.groupName || '';
        if (!name) {
            return null;
        }

        return {
            name,
            id: String(groupId)
        };
    }
}
